import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { GetUsersMe200Response } from '@src/api/generated/user-api'
import { PATHS } from '@src/consts/index'
import { Tfa } from '@src/features/invoke/redirect/web/page/invoke-redirect-web-main/type'
import { useSignout } from '@src/hooks/queries'
import {
  canUseAvailableFeature,
  getMailAddress,
  getSmsPhoneNumber,
  getUser,
  isAdboostOrganization,
} from '@src/utils'
import { OAuthResponseType } from '@src/utils/oauth'
import {
  isOAuthReauthRequired,
  isOAuthRequest,
  setOAuthClientParams,
} from '@src/utils/oauth/oauth'

import { entrancePathNames } from './entrance-path-names'
import { notAllowedPathNames } from './not-allowed-path-names'
import { tfaPathNames } from './tfa-path-names'

const redirectToNuxtPath = async (path: string) => {
  const { IS_STORYBOOK } = getConfig()
  // This is necessary to avoid REPLACEMENT when testing redirections in the storybook.
  if (IS_STORYBOOK) {
    history.replaceState('', '', path)
    return
  }
  await window.location.replace(path)
}

export function homePagePath(user: GetUsersMe200Response): Promise<void> {
  if (
    (user.isOrganizationAdmin || user.isViewer || user.isBookkeeper) &&
    user.hasCardContract
  ) {
    return redirectToNuxtPath(PATHS.CARDS)
  }

  if (
    (user.isOrganizationAdmin || user.isViewer || user.isBookkeeper) &&
    user.hasPayContract
  ) {
    return redirectToNuxtPath(PATHS.PAYMENTS)
  }

  if (user.isGeneralUser && user.hasCardContract) {
    return redirectToNuxtPath(PATHS.USER_CARDS)
  }

  return redirectToNuxtPath('/')
}

const isFreshdeskRedirect = (
  router: ReturnType<typeof useRouter>,
  currentPathName: string
) => {
  return (
    router.query.refererService === 'freshdesk' &&
    currentPathName === PATHS.SIGNIN
  )
}

const isOauthRedirect = (
  router: ReturnType<typeof useRouter>,
  currentPathName: string
) => {
  return (
    !!router.query.login_challenge &&
    typeof router.query.login_challenge === 'string' &&
    currentPathName === PATHS.SIGNIN
  )
}

const isOAuthSignin = (currentPathName: string) => {
  return currentPathName === PATHS.SIGNIN && isOAuthRequest()
}

const isUserCardsPage = (currentPathName: string) => {
  return currentPathName.startsWith(PATHS.USER_CARDS)
}

export const useRedirectRules = () => {
  const router = useRouter()
  const { mutate } = useSignout()

  const redirectIfNotAccess = async () => {
    const user = getUser()

    // ■ Perform type guard
    if (!router) return
    const currentPathName = router.pathname

    // ■ Persist client information for oauth
    if (currentPathName === PATHS.SIGNIN) {
      setOAuthClientQueryParams()
    }

    // ■ Check if the user is not logged in but accessing the auhorized pages
    if (!user) {
      if (entrancePathNames.includes(currentPathName)) return
      if (canAccessTfaPages(currentPathName)) return

      const backUrl = getBackUrl()
      mutate({ backUrl, isSessionTimeout: false })
      return
    }

    // ■ Check if the user is logged in but accessing the entrance pages
    if (
      entrancePathNames.includes(currentPathName) ||
      tfaPathNames.includes(currentPathName)
    ) {
      // ■ Check when redirected from freshdesk
      // stay on the entrace pages to continue sso process.
      if (isFreshdeskRedirect(router, currentPathName)) return

      // ■ Check when redirected from oauth
      // stay on the entrace pages to continue oauth process.
      if (isOauthRedirect(router, currentPathName)) return

      // ■ Check when OAuth re-authentication (prompt=login) is in progress.
      // Keep the user on the current sign-in / MFA page so they can complete
      // re-authentication (ID/Password + MFA), even when a previous session is
      // still active. Without this, a logged-in user is redirected to the home
      // page from the MFA pages (/signin/tfa/*) and the OAuth grant-code flow
      // never completes. The flag is read from the persisted OAuth params, so it
      // works on the MFA pages too, which do not carry the query string.
      if (isOAuthReauthRequired()) {
        return
      }

      // ■ Check when came for upsider oauth
      // redirect to the grant code page.
      if (isOAuthSignin(currentPathName)) {
        router.push(PATHS.SIGNIN_GRANT_CODE)
        return
      }
      // Redirect to the home page.
      await homePagePath(user)
      return
    }

    // ■ Check if it is a error page
    // TODO: I just copied the Nuxt code and will need to rewrite it later.
    // if (isErrorPage(path)) return

    // ■ Check card contract and pay contract status
    if (!user.hasCardContract && !user.hasPayContract) {
      const backUrl = getBackUrl()
      mutate({ backUrl, isSessionTimeout: false })
      return
    }

    // ■ Check password registration status
    // TODO: When SAML information can be retrieved by user-api, add a condition whether SAML is enforced or not.
    if (user.isTempPassword) {
      if (!currentPathName.startsWith(PATHS.REGISTER_PASSWORD)) {
        return redirectToNuxtPath(PATHS.REGISTER_PASSWORD)
      }
      return
    }

    // ■ Redirect Adboost organizations trying to access restricted card issuance pages
    // Adboost organizations can only issue virtual cards, not physical/on-demand cards
    if (isAdboostOrganization(user)) {
      if (
        currentPathName === PATHS.CARDS_ISSUE_PHYSICAL ||
        currentPathName === PATHS.CARDS_ISSUE_ON_DEMAND
      ) {
        // Redirect to appropriate card page based on user role
        const redirectPath = user.isGeneralUser ? PATHS.USER_CARDS : PATHS.CARDS
        await redirectToNuxtPath(redirectPath)
        return
      }
    }

    // ■ Check feature flag access for OL consent page
    if (currentPathName === PATHS.OL_CONSENT) {
      if (!canUseAvailableFeature('ONLINE_LENDING')) {
        await homePagePath(user)
        return
      }
    }

    if (isUserCardsPage(currentPathName)) {
      if (user.isGeneralUser) {
        return
      }

      if (currentPathName === PATHS.USER_CARDS_TRANSACTIONS) {
        await redirectToNuxtPath(PATHS.TRANSACTIONS)
        return
      }
    }

    // ■ Access control per role
    if (user.isOrganizationAdmin) {
      return
    }

    if (user.isGeneralUser) {
      if (currentPathName === PATHS.TRANSACTIONS) {
        await redirectToNuxtPath(PATHS.USER_CARDS_TRANSACTIONS)
        return
      }

      const fullPath = router.asPath
      // FIXME: This is a temporary redirect for only transactions.
      // Basically, this kind of redirect should NOT be written in this file.
      const match = matchCardsTransactionsPattern(fullPath)
      if (match.isMatched) {
        const [userAccountId, transactionId, toYearMonth] = match.matchedParams
        await redirectToNuxtPath(
          PATHS.USER_CARDS_TRANSACTIONS_DETAILS({
            userAccountId,
            transactionId,
            toYearMonth,
          })
        )
        return
      }

      const renewMatch = matchCardsRenewPattern(fullPath)
      if (renewMatch.isMatched) {
        const [userAccountId] = renewMatch.matchedParams
        router.push(PATHS.USER_CARDS_RENEW(userAccountId))
        return
      }

      const fraudUnlockMatch = matchFraudUnlockPattern(fullPath)
      if (fraudUnlockMatch.isMatched) {
        const [userAccountId] = fraudUnlockMatch.matchedParams
        router.push(PATHS.USER_CARDS_FRAUD_UNLOCK({ userAccountId }))
        return
      }

      if (
        notAllowedPathNames.generalUser.some((v) =>
          currentPathName.startsWith(v)
        )
      ) {
        await redirectToNuxtPath(PATHS.USER_CARDS)
        return
      }
    }
    if (user.isBookkeeper) {
      if (
        notAllowedPathNames.bookkeeper.some((v) =>
          currentPathName.startsWith(v)
        )
      ) {
        await redirectToNuxtPath(PATHS.CARDS)
        return
      }
    }
    if (user.isViewer) {
      if (
        notAllowedPathNames.viewer.some((v) => currentPathName.startsWith(v))
      ) {
        await redirectToNuxtPath(PATHS.CARDS)
        return
      }
      return
    }
  }

  function setOAuthClientQueryParams() {
    setOAuthClientParams({
      clientId: router.query.client_id as string,
      responseType: router.query.response_type as OAuthResponseType,
      redirectUrl: router.query.redirect_url as string,
      state: router.query.state as string,
      // Persist prompt=login so the re-auth requirement survives navigation to
      // the MFA pages, where the query string is not carried over.
      prompt: router.query.prompt === 'login' ? 'login' : undefined,
    })
  }

  function canAccessTfaPages(currentPathName: string): boolean {
    //TODO: use zod to validate query
    const tfaType = router.query.tfa as Tfa
    const tfaToken = router.query.tfaToken as string
    const mailAddress =
      typeof router.query.email === 'string'
        ? router.query.email
        : getMailAddress()
    const smsPhoneNumberQuery = router.query.smsPhoneNumber
    const smsPhoneNumber = smsPhoneNumberQuery ?? getSmsPhoneNumber()

    if (currentPathName === PATHS.SIGNIN_TFA_APP) {
      return !!mailAddress || (tfaType === 'app' && !!tfaToken)
    }
    if (currentPathName === PATHS.SIGNIN_TFA_SMS) {
      return !!smsPhoneNumber || (tfaType === 'sms' && !!tfaToken)
    }
    if (currentPathName === PATHS.SIGNIN_TFA_RECOVERY_CODE) {
      return !!mailAddress || !!smsPhoneNumber
    }
    return false
  }

  function matchCardsTransactionsPattern(path: string):
    | {
        isMatched: true
        matchedParams: [string, string, string?]
      }
    | {
        isMatched: false
      } {
    // NOTE: Please be sure to ensure that it works with or without a slash before ?yearMonth= when you fix it below.
    const pattern =
      /\/cards\/users\/(.+)\/transactions\/([^/?]+)(\/?\?yearMonth=(.+))?/
    const match = path.match(pattern)
    if (match) {
      const [
        ,
        userAccountId,
        transactionId /* ?yearMonth=xxxxxx */,
        ,
        toYearMonth,
      ] = match
      return {
        isMatched: true,
        matchedParams: [userAccountId, transactionId, toYearMonth],
      }
    }
    return { isMatched: false }
  }

  function matchCardsRenewPattern(path: string):
    | {
        isMatched: true
        matchedParams: [string]
      }
    | {
        isMatched: false
      } {
    const pattern = /^\/?(cards|user-cards)\/([^/]+)\/renew\/?$/

    const match = path.match(pattern)
    if (match) {
      const [
        ,
        , // cards or user-cards
        userAccountId,
      ] = match
      return {
        isMatched: true,
        matchedParams: [userAccountId],
      }
    }
    return { isMatched: false }
  }

  function matchFraudUnlockPattern(path: string):
    | {
        isMatched: true
        matchedParams: [string]
      }
    | {
        isMatched: false
      } {
    const pattern = /^\/(?:cards\/users|user-cards)\/([^/]+)\/fraud\/unlock\/?$/

    const match = path.match(pattern)
    if (match) {
      const [, userAccountId] = match
      return {
        isMatched: true,
        matchedParams: [userAccountId],
      }
    }
    return { isMatched: false }
  }

  function getBackUrl(): string {
    // FIXME: Please extend or modify the code below to include other paths as well.
    const backUrl = shouldReturnAsPath(router.pathname)
      ? router.asPath // To get exact path including path parameters when the path is dynamic since router.pathname returns like /path/[id]
      : router.pathname + window.location.search + window.location.hash
    return encodeURIComponent(backUrl)
  }

  function shouldReturnAsPath(path: string): boolean {
    const cardRenewalPathPattern = /^\/?(cards|user-cards)\/[^/]+\/renew\/?$/
    if (cardRenewalPathPattern.test(path)) {
      return true
    }

    const fraudUnlockMatch = matchFraudUnlockPattern(path)
    if (fraudUnlockMatch.isMatched) {
      return true
    }

    if (path.startsWith(PATHS.MESSAGES)) {
      return true
    }

    return false
  }

  // The entire application uses useEffect to run on the client due to SSR
  useEffect(() => {
    redirectIfNotAccess()
    // It is set to disable because you only want to run it the first time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
